import React from "react";
import {
  Text,
  Title,
  Badge,
  Stack,
  Group,
  Grid,
  Switch,
  Tooltip,
  Box,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { getPayrollSourceIdForApi, isNativePayrollBid } from "./payrollSourceHelpers";
import EntityTabAnchor from "./EntityTabAnchor";

const getDnStatusColor = (status) => {
  switch (status) {
    case "DFT":
      return "yellow";
    case "WAIT":
    case "HOLD":
      return "red";
    case "CONFIRMED":
    case "SETTLED":
      return "blue";
    case "EXP":
      return "green";
    case "CAN":
      return "orange";
    default:
      return "blue";
  }
};

const getPaymentStatusColor = (status) => {
  switch (status) {
    case "PAID":
      return "green";
    case "SETTLED":
    case "DIRECT":
      return "blue";
    case "PARTIAL":
    case "MATCHING":
      return "orange";
    case "DFT":
      return "yellow";
    case "WAIT":
      return "red";
    default:
      return "gray";
  }
};

const LabelValueRow = ({ label, children, labelMinWidth = 72 }) => (
  <Group spacing="xs" noWrap align="center" sx={{ minWidth: 0 }}>
    <Text
      size="xs"
      color="dimmed"
      sx={{ minWidth: labelMinWidth, flexShrink: 0 }}
    >
      {label}:
    </Text>
    {children}
  </Group>
);

const ByOrderRowHeader = ({
  item,
  formatter,
  cellRender,
  onHold,
  loading,
  isConfirmed,
  payrollId,
}) => {
  const { t } = useTranslation();
  const bidsArray = (item.bids || []).filter((b) =>
    isNativePayrollBid(b, payrollId),
  );
  const heldCountFromBids = bidsArray.filter((b) => b.status === "HOLD").length;
  const nativeBidCount =
    bidsArray.length > 0 ? bidsArray.length : item.bidCount || 0;
  const heldCount =
    bidsArray.length > 0
      ? heldCountFromBids
      : item.holdCount ??
        item.bids?.filter((b) => b.status === "HOLD").length ??
        0;
  const isAllHeld = heldCount === nativeBidCount && nativeBidCount > 0;

  const isCommissionStatement = item.sourceType === "COMMISSIONSTATEMENT";
  const isClientPayment =
    item.sourceType === "CLIENTPAYMENT" || item.sourceType === "ADJUSTMENT";

  const dnStatus = item.debitNote?.status ?? item.filterMeta?.dnStatus;
  const paymentStatus =
    item.debitNote?.paymentStatus ?? item.filterMeta?.dnPaymentStatus;
  const orderStatus = item.order?.status ?? item.filterMeta?.orderStatus;

  const client = item.client || item.debitNote?.client || item.order?.client;
  const productName = item.product?.name || item.product?.code;
  const productClass =
    item.filterMeta?.productType?.name ||
    item.filterMeta?.productCategory?.name ||
    item.product?.cname;
  const productLabel = [productName, productClass].filter(Boolean).join(" / ");

  const stopNav = (e) => e.stopPropagation();

  return (
    <Grid justify="space-between" align="center">
      <Grid.Col span={5}>
        <Stack spacing={6}>
          {isClientPayment && (
            <Group spacing="xs">
              <Badge size="sm" color="blue" variant="light">
                {item.sourceType === "ADJUSTMENT"
                  ? "Adjustment"
                  : "Client Payment"}
              </Badge>
              {heldCount > 0 && (
                <Badge size="xs" color="yellow" variant="filled">
                  {heldCount} held
                </Badge>
              )}
            </Group>
          )}

          {isCommissionStatement && (
            <Group spacing="xs">
              <Badge size="sm" color="violet" variant="light">
                Commission Statement
              </Badge>
              {item.commissionStatement ? (
                <EntityTabAnchor
                  entity="commissionStatement"
                  id={item.commissionStatement._id}
                  label={item.commissionStatement.code}
                  size="sm"
                  weight={500}
                  onClick={stopNav}
                />
              ) : (
                <Text size="sm" weight={500} color="dimmed">
                  —
                </Text>
              )}
              {heldCount > 0 && (
                <Badge size="xs" color="yellow" variant="filled">
                  {heldCount} held
                </Badge>
              )}
            </Group>
          )}

          {item.debitNote && (
            <LabelValueRow label="Debit Note">
              <EntityTabAnchor
                entity="debitNote"
                id={item.debitNote._id}
                label={item.debitNote.code}
                size="xs"
                weight={500}
                onClick={stopNav}
              />
              {dnStatus && (
                <Badge size="xs" radius={0} color={getDnStatusColor(dnStatus)}>
                  {t(dnStatus)}
                </Badge>
              )}
              {isClientPayment && paymentStatus && (
                <Badge
                  size="xs"
                  radius={0}
                  color={getPaymentStatusColor(paymentStatus)}
                >
                  {paymentStatus}
                </Badge>
              )}
            </LabelValueRow>
          )}

          {!item.debitNote && item.order && (
            <LabelValueRow label="Order">
              <EntityTabAnchor
                entity="order"
                id={item.order._id}
                label={item.order.code}
                size="xs"
                weight={500}
                onClick={stopNav}
              />
              {orderStatus && cellRender?.Order?.status ? (
                cellRender.Order.status(orderStatus)
              ) : orderStatus ? (
                <Badge size="xs" radius={0} color="blue">
                  {t(orderStatus)}
                </Badge>
              ) : null}
            </LabelValueRow>
          )}

          {client && (
            <LabelValueRow label="Client">
              <Tooltip
                label={client.name || client.code}
                disabled={!(client.name || client.code)}
                withArrow
                multiline
                maw={420}
              >
                <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <EntityTabAnchor
                    entity="client"
                    id={client._id}
                    label={client.name || client.code}
                    size="xs"
                    weight={500}
                    onClick={stopNav}
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Box>
              </Tooltip>
              {client.code && (
                <Badge
                  size="xs"
                  variant="outline"
                  color="gray"
                  sx={{ flexShrink: 0 }}
                >
                  {client.code}
                </Badge>
              )}
            </LabelValueRow>
          )}

          {productLabel && (
            <LabelValueRow label="Product">
              <Text size="xs" weight={500}>
                {productLabel}
              </Text>
            </LabelValueRow>
          )}

          {isCommissionStatement && item.internalRemark && (
            <LabelValueRow label="Internal Remark" labelMinWidth={100}>
              <Tooltip label={item.internalRemark} withArrow multiline maw={420}>
                <Text
                  size="xs"
                  color="orange"
                  lineClamp={2}
                  sx={{ minWidth: 0 }}
                >
                  {item.internalRemark}
                </Text>
              </Tooltip>
            </LabelValueRow>
          )}
        </Stack>
      </Grid.Col>
      <Grid.Col span={2}>
        <Stack align="flex-start" spacing="xs">
          <Title order={3}>{nativeBidCount}</Title>
          <Text size="xs" color="dimmed">
            Total bids
          </Text>
        </Stack>
      </Grid.Col>
      <Grid.Col span={2}>
        <Stack align="flex-start" spacing="xs">
          <Title order={3} color={item.totalAmount >= 0 ? "green" : "red"}>
            {formatter.currency(item.totalAmount)}
          </Title>
          <Text size="xs" color="dimmed">
            Total Amount
          </Text>
        </Stack>
      </Grid.Col>
      <Grid.Col span={2}>
        <div onClick={(e) => e.stopPropagation()}>
          <Tooltip
            label={
              isConfirmed
                ? "Payroll completed"
                : isAllHeld
                  ? "Release all bids"
                  : "Hold all bids"
            }
          >
            <Switch
              size="md"
              color="yellow"
              checked={isAllHeld}
              disabled={loading || isConfirmed}
              onChange={() =>
                onHold &&
                onHold(getPayrollSourceIdForApi(item), item.sourceType, !isAllHeld)
              }
              label={
                <Text size="xs" color="dimmed">
                  Hold
                </Text>
              }
            />
          </Tooltip>
        </div>
      </Grid.Col>
    </Grid>
  );
};

export default ByOrderRowHeader;
